import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Seitenkopf } from '../components/Seitenkopf';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { IconAuswertung } from '../components/icons';
import { DEMO, api, ApiFehler } from '../lib/api';
import { formatEuro } from '../lib/format';

interface ObjektZeile {
  objektId: string;
  bezeichnung: string;
  jahresKaltmieteCent: number;
  nichtUmlagefaehigeKostenCent: number;
  ueberschussCent: number;
  mietspiegelPotenzialCent: number;
  margeProzent: number;
  jahreszinsCent?: number;
  jahrestilgungCent?: number;
  kapitaldienstCent?: number;
  cashflowNachKapitaldienstCent?: number;
}

interface Summe {
  jahresKaltmieteCent: number;
  nichtUmlagefaehigeKostenCent: number;
  ueberschussCent: number;
  mietspiegelPotenzialCent: number;
  margeProzent: number;
  objekteAnzahl: number;
  kapitaldienstCent?: number;
  cashflowNachKapitaldienstCent?: number;
}

interface Antwort {
  jahr: number;
  objekte: ObjektZeile[];
  summe: Summe;
}

const JETZT = new Date().getUTCFullYear();

function demoAntwort(jahr: number): Antwort {
  const objekte: ObjektZeile[] = [
    {
      objektId: 'obj-1',
      bezeichnung: 'Lindenstraße 14',
      jahresKaltmieteCent: 5_712_00,
      nichtUmlagefaehigeKostenCent: 1_284_00,
      ueberschussCent: 4_428_00,
      mietspiegelPotenzialCent: 612_00,
      margeProzent: 77.5,
      jahreszinsCent: 1_050_00,
      jahrestilgungCent: 600_00,
      kapitaldienstCent: 1_650_00,
      cashflowNachKapitaldienstCent: 2_778_00,
    },
    {
      objektId: 'obj-2',
      bezeichnung: 'Am Wasserturm 3',
      jahresKaltmieteCent: 3_240_00,
      nichtUmlagefaehigeKostenCent: 918_00,
      ueberschussCent: 2_322_00,
      mietspiegelPotenzialCent: 0,
      margeProzent: 71.7,
      jahreszinsCent: 0,
      jahrestilgungCent: 0,
      kapitaldienstCent: 0,
      cashflowNachKapitaldienstCent: 2_322_00,
    },
  ];
  const summe = objekte.reduce(
    (a, o) => ({
      jahresKaltmieteCent: a.jahresKaltmieteCent + o.jahresKaltmieteCent,
      nichtUmlagefaehigeKostenCent: a.nichtUmlagefaehigeKostenCent + o.nichtUmlagefaehigeKostenCent,
      ueberschussCent: a.ueberschussCent + o.ueberschussCent,
      mietspiegelPotenzialCent: a.mietspiegelPotenzialCent + o.mietspiegelPotenzialCent,
      kapitaldienstCent: a.kapitaldienstCent + (o.kapitaldienstCent ?? 0),
      cashflowNachKapitaldienstCent:
        a.cashflowNachKapitaldienstCent + (o.cashflowNachKapitaldienstCent ?? 0),
      margeProzent: 0,
      objekteAnzahl: 0,
    }),
    {
      jahresKaltmieteCent: 0,
      nichtUmlagefaehigeKostenCent: 0,
      ueberschussCent: 0,
      mietspiegelPotenzialCent: 0,
      kapitaldienstCent: 0,
      cashflowNachKapitaldienstCent: 0,
      margeProzent: 0,
      objekteAnzahl: 0,
    },
  );
  summe.objekteAnzahl = objekte.length;
  summe.margeProzent =
    summe.jahresKaltmieteCent > 0
      ? Math.round((summe.ueberschussCent / summe.jahresKaltmieteCent) * 1000) / 10
      : 0;
  return { jahr, objekte, summe };
}

/** Zwei-Segment-Donut: Überschuss (Akzent) und nicht umlagefähige Kosten (gedämpft). */
function Donut({ ueberschuss, kosten }: { ueberschuss: number; kosten: number }) {
  const basis = Math.max(ueberschuss, 0) + Math.max(kosten, 0);
  const r = 80;
  const c = 2 * Math.PI * r;
  const anteilUeberschuss = basis > 0 ? Math.max(ueberschuss, 0) / basis : 0;
  const negativ = ueberschuss < 0;

  const segmente = negativ
    ? [{ frac: 1, farbe: 'rgb(var(--c-danger))' }]
    : [
        { frac: anteilUeberschuss, farbe: 'rgb(var(--c-accent))' },
        { frac: 1 - anteilUeberschuss, farbe: 'rgb(var(--c-fg-subtle))' },
      ];

  let offset = 0;
  return (
    <svg viewBox="0 0 200 200" className="h-52 w-52 -rotate-90">
      <circle cx="100" cy="100" r={r} fill="none" stroke="rgb(var(--c-surface-3))" strokeWidth="22" />
      {segmente.map((s, i) => {
        const laenge = s.frac * c;
        const el = (
          <motion.circle
            key={i}
            cx="100"
            cy="100"
            r={r}
            fill="none"
            stroke={s.farbe}
            strokeWidth="22"
            strokeLinecap="butt"
            strokeDasharray={`${laenge} ${c - laenge}`}
            initial={{ strokeDashoffset: -c * offset + c }}
            animate={{ strokeDashoffset: -c * offset }}
            transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1], delay: 0.1 * i }}
          />
        );
        offset += s.frac;
        return el;
      })}
    </svg>
  );
}

export function Auswertung() {
  const [jahr, setJahr] = useState(JETZT);
  const [daten, setDaten] = useState<Antwort | null>(DEMO ? demoAntwort(JETZT) : null);
  const [laden, setLaden] = useState(!DEMO);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    if (DEMO) {
      setDaten(demoAntwort(jahr));
      return;
    }
    setLaden(true);
    api
      .get<Antwort>(`/auswertung/profitabilitaet?jahr=${jahr}`)
      .then((d) => {
        setDaten(d);
        setFehler(null);
      })
      .catch((e) => setFehler(e instanceof ApiFehler ? e.message : 'Die Auswertung konnte nicht geladen werden.'))
      .finally(() => setLaden(false));
  }, [jahr]);

  const jahre = useMemo(() => [JETZT, JETZT - 1, JETZT - 2], []);
  const s = daten?.summe;
  const hatDaten = s && s.jahresKaltmieteCent > 0;

  return (
    <div>
      <Seitenkopf
        titel="Auswertung"
        beschreibung="Profitabilität je Objekt und übers Portfolio: Jahreskaltmiete, selbst getragene (nicht umlagefähige) Kosten, Überschuss vor Kapitaldienst und ungenutztes Mietspiegel-Potenzial."
      />

      <div className="mb-4 flex items-center gap-2">
        <span className="text-sm font-medium text-fg-muted">Jahr</span>
        <div className="flex gap-1 rounded-lg border border-border bg-surface p-1">
          {jahre.map((j) => (
            <button
              key={j}
              onClick={() => setJahr(j)}
              className={`rounded-md px-3 py-1 text-sm font-medium transition ${
                j === jahr ? 'bg-accent text-accent-fg' : 'text-fg-muted hover:text-fg'
              }`}
            >
              {j}
            </button>
          ))}
        </div>
      </div>

      {fehler && !daten && (
        <Card>
          <p className="text-sm text-danger">{fehler}</p>
        </Card>
      )}

      {laden && !daten ? (
        <p className="text-sm text-fg-muted">Wird geladen …</p>
      ) : !hatDaten ? (
        <EmptyState
          icon={<IconAuswertung groesse={22} />}
          titel="Noch keine Auswertung möglich"
          beschreibung="Sobald Objekte mit laufenden Mietverhältnissen erfasst sind, erscheint hier die Profitabilität mit Überschuss und Mietspiegel-Potenzial."
        />
      ) : (
        <div className="space-y-6">
          {/* Portfolio-Überblick */}
          <Card>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-[auto_1fr] md:items-center">
              <div className="relative flex items-center justify-center">
                <Donut ueberschuss={s!.ueberschussCent} kosten={s!.nichtUmlagefaehigeKostenCent} />
                <div className="absolute flex flex-col items-center">
                  <span className="text-xs uppercase tracking-wide text-fg-subtle">Überschuss</span>
                  <span className="tabellenziffern text-2xl font-bold text-fg">
                    {formatEuro(s!.ueberschussCent)}
                  </span>
                  <span className="text-sm font-medium text-accent">{s!.margeProzent.toLocaleString('de-DE')} % Marge</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Kennzahl
                  label="Jahreskaltmiete (Soll)"
                  wert={formatEuro(s!.jahresKaltmieteCent)}
                  farbe="fg"
                  punkt="accent"
                />
                <Kennzahl
                  label="Nicht umlagefähige Kosten"
                  wert={formatEuro(s!.nichtUmlagefaehigeKostenCent)}
                  farbe="fg"
                  punkt="subtle"
                />
                <Kennzahl
                  label="Überschuss vor Kapitaldienst"
                  wert={formatEuro(s!.ueberschussCent)}
                  farbe={s!.ueberschussCent >= 0 ? 'fg' : 'danger'}
                />
                <Kennzahl
                  label="Mietspiegel-Potenzial / Jahr"
                  wert={formatEuro(s!.mietspiegelPotenzialCent)}
                  farbe={s!.mietspiegelPotenzialCent > 0 ? 'accent' : 'fg'}
                  hinweis={s!.mietspiegelPotenzialCent > 0 ? 'zusätzlich rechtlich denkbar' : 'ausgeschöpft'}
                />
                {(s!.kapitaldienstCent ?? 0) > 0 && (
                  <>
                    <Kennzahl label="Kapitaldienst / Jahr" wert={formatEuro(s!.kapitaldienstCent ?? 0)} farbe="fg" punkt="subtle" />
                    <Kennzahl
                      label="Cashflow nach Finanzierung"
                      wert={formatEuro(s!.cashflowNachKapitaldienstCent ?? 0)}
                      farbe={(s!.cashflowNachKapitaldienstCent ?? 0) >= 0 ? 'accent' : 'danger'}
                      hinweis="nach Zins und Tilgung"
                    />
                  </>
                )}
              </div>
            </div>
          </Card>

          {/* Objektliste */}
          <section>
            <h2 className="mb-2 px-1 text-sm font-semibold uppercase tracking-wide text-fg-subtle">
              Je Objekt ({s!.objekteAnzahl})
            </h2>
            <div className="space-y-2.5">
              {daten!.objekte.map((o) => {
                const basis = Math.max(o.ueberschussCent, 0) + Math.max(o.nichtUmlagefaehigeKostenCent, 0);
                const anteil = basis > 0 ? Math.max(o.ueberschussCent, 0) / basis : 0;
                return (
                  <Card key={o.objektId}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-semibold text-fg">{o.bezeichnung}</span>
                          <Badge ton={o.ueberschussCent >= 0 ? 'erfolg' : 'warnung'}>
                            {o.margeProzent.toLocaleString('de-DE')} % Marge
                          </Badge>
                          {o.mietspiegelPotenzialCent > 0 && (
                            <Badge ton="info">+{formatEuro(o.mietspiegelPotenzialCent)} Potenzial</Badge>
                          )}
                        </div>
                        <p className="mt-1 text-xs text-fg-subtle">
                          Kaltmiete {formatEuro(o.jahresKaltmieteCent)} · Kosten{' '}
                          {formatEuro(o.nichtUmlagefaehigeKostenCent)}
                          {(o.kapitaldienstCent ?? 0) > 0 && (
                            <>
                              {' '}· Kapitaldienst {formatEuro(o.kapitaldienstCent ?? 0)} · Cashflow{' '}
                              <span className={(o.cashflowNachKapitaldienstCent ?? 0) >= 0 ? 'text-success' : 'text-danger'}>
                                {formatEuro(o.cashflowNachKapitaldienstCent ?? 0)}
                              </span>
                            </>
                          )}
                        </p>
                        <div className="mt-2 h-2 w-56 max-w-full overflow-hidden rounded-pill bg-surface-3">
                          <div
                            className="h-full rounded-pill bg-accent"
                            style={{ width: `${Math.round(anteil * 100)}%` }}
                          />
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs uppercase tracking-wide text-fg-subtle">Überschuss</p>
                        <p
                          className={`tabellenziffern text-lg font-semibold ${
                            o.ueberschussCent >= 0 ? 'text-fg' : 'text-danger'
                          }`}
                        >
                          {formatEuro(o.ueberschussCent)}
                        </p>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </section>

          <p className="px-1 text-xs text-fg-subtle">
            Überschuss vor Kapitaldienst (ohne Finanzierung, Abschreibung und Steuer). Umlagefähige
            Kosten laufen durch und sind hier nicht enthalten. Das Mietspiegel-Potenzial ist eine
            Kennzahl, keine Rechtsberatung.
          </p>
        </div>
      )}
    </div>
  );
}

function Kennzahl({
  label,
  wert,
  farbe,
  punkt,
  hinweis,
}: {
  label: string;
  wert: string;
  farbe: 'fg' | 'accent' | 'danger';
  punkt?: 'accent' | 'subtle';
  hinweis?: string;
}) {
  const farbklasse = farbe === 'accent' ? 'text-accent' : farbe === 'danger' ? 'text-danger' : 'text-fg';
  return (
    <div className="rounded-lg border border-border bg-surface-2 p-3">
      <div className="flex items-center gap-1.5">
        {punkt && (
          <span
            className={`h-2.5 w-2.5 rounded-pill ${punkt === 'accent' ? 'bg-accent' : 'bg-fg-subtle'}`}
          />
        )}
        <span className="text-xs text-fg-muted">{label}</span>
      </div>
      <p className={`mt-1 tabellenziffern text-lg font-semibold ${farbklasse}`}>{wert}</p>
      {hinweis && <p className="text-xs text-fg-subtle">{hinweis}</p>}
    </div>
  );
}

export default Auswertung;
